/**
 * emphasis.js — `*` around a selection, and the Ctrl+B / Ctrl+I toggles.
 */

import { EditorSelection } from '@codemirror/state';
import { EditorView } from '@codemirror/view';

// A run of k stars is bold when k >= 2 and italic when k is odd, so `***x***`
// is both and each toggle takes off only its own share.
const has = (mark, k) => (mark.length === 2 ? k >= 2 : k % 2 === 1);

const toggleMark = (mark) => ({ state, dispatch }) => {
  if (state.readOnly) return false;
  const n = mark.length;
  dispatch(state.update(state.changeByRange((range) => {
    const { from, to, anchor, head } = range;
    const before = state.sliceDoc(state.doc.lineAt(from).from, from);
    const after = state.sliceDoc(to, state.doc.lineAt(to).to);
    const outside = Math.min(/\**$/.exec(before)[0].length, /^\**/.exec(after)[0].length);
    if (has(mark, outside)) {
      return {
        changes: [{ from: from - n, to: from }, { from: to, to: to + n }],
        range: EditorSelection.range(anchor - n, head - n),
      };
    }
    // The selection may also have been drawn around the markers themselves.
    const text = state.sliceDoc(from, to);
    const inside = Math.min(/^\**/.exec(text)[0].length, /\**$/.exec(text)[0].length);
    if (text.length > 2 * inside && has(mark, inside)) {
      return {
        changes: [{ from, to: from + n }, { from: to - n, to }],
        range: EditorSelection.range(Math.min(anchor, to - 2 * n), Math.min(head, to - 2 * n)),
      };
    }
    return {
      changes: [{ from, insert: mark }, { from: to, insert: mark }],
      range: EditorSelection.range(anchor + n, head + n),
    };
  }), { scrollIntoView: true, userEvent: 'input' }));
  return true;
};

export const emphasisKeymap = [
  { key: 'Mod-b', run: toggleMark('**') },
  { key: 'Mod-i', run: toggleMark('*') },
];

// Typing `*` over a selection wraps it and keeps it selected, so a second `*`
// makes it bold.
export const starWrapsSelection = EditorView.inputHandler.of((view, _from, _to, text) => {
  const { state } = view;
  if (text !== '*' || state.selection.ranges.every((r) => r.empty)) return false;
  view.dispatch(state.changeByRange((range) => (range.empty
    ? { changes: { from: range.from, insert: '*' }, range: EditorSelection.cursor(range.from + 1) }
    : {
        changes: [{ from: range.from, insert: '*' }, { from: range.to, insert: '*' }],
        range: EditorSelection.range(range.anchor + 1, range.head + 1),
      })), { scrollIntoView: true, userEvent: 'input.type' });
  return true;
});
